import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { t } from 'ttag';
import { useImmer } from 'use-immer';
import PropTypes from 'prop-types';
import { DialogBox, Price } from 'components';
import { RiCloseLine } from 'react-icons/ri';
import qs from 'qs';
import { useCustomization } from 'hooks';
import { CLEAR_CUSTOMIZATION } from '../../productpage/appActions';
import UNIID from '../../sharedSvgs/uniid.svg';
import getObject from './utils/getObject';
import client from '../../utils/client';
import { UPDATE_CART_DATA } from '../../cart/actions/actions';

function CustomizationSummary({
  itemData,
  productID,
  isRemovable,
  showIfNoCustomizationAdded,
}) {
  const [customization, updateCustomization] = useImmer(
    /** @type {Object[]} */ []
  );
  const [isDialogVisible, toggleDialog] = useState(false);
  const [canBeRemoved, toggleCanBeRemoved] = useState(isRemovable);

  const selectedCustomization = useSelector(
    (state) => state.app.customization.customizationList,
    shallowEqual
  );

  const dispatch = useDispatch();

  const { data: customizationData } = useCustomization(
    productID ||
      (window.__INITIAL_STATE__ && window.__INITIAL_STATE__.product_id)
  );

  useEffect(() => {
    updateCustomization([]);

    if (itemData && itemData.sublines && Array.isArray(itemData.sublines)) {
      itemData.sublines.forEach((subline) => {
        if (!subline.product_id) {
          updateCustomization((draft) => {
            draft.push({
              name: subline.product,
              price: subline.line_total,
            });
          });
        }
        if (canBeRemoved && subline.product_id) {
          toggleCanBeRemoved(false);
        }
      });
    }

    if (!itemData && customizationData && selectedCustomization) {
      const { name, number, player_flag, marker } = selectedCustomization;

      if (player_flag) {
        const { price } = getObject(customizationData, 'name', 'player_flag');
        const {
          title,
          number: playerNumber,
          name: playerName,
          original: isOriginal,
        } = getObject(customizationData, 'value', player_flag);

        updateCustomization((draft) => {
          draft.push({
            name: `${t`Print`}: ${
              title ||
              `${playerName}, ${playerNumber} ${isOriginal ? '(Original)' : ''}`
            }`,
            price,
          });
        });
      }

      if (name || number) {
        const nameOption = getObject(customizationData, 'name', 'name');
        const numberOption = getObject(customizationData, 'name', 'number');

        const namePrice = nameOption && nameOption.price;
        const numberPrice = numberOption && numberOption.price;

        updateCustomization((draft) => {
          draft.push({
            name: `${t`Print`}: ${
              name ? `${name.toUpperCase()}${name && number ? ', ' : ''}` : ''
            }${number || ''}`,
            price: namePrice || numberPrice,
          });
        });
      }

      if (marker) {
        const { addon_name, price } = getObject(
          customizationData,
          'name',
          'marker'
        );

        updateCustomization((draft) => {
          draft.push({
            name: addon_name,
            price,
          });
        });
      }
    }
  }, [itemData, customizationData, selectedCustomization]);

  const clearCustomization = useCallback(() => {
    if (itemData) {
      client
        .post(`/customizations/update/${itemData.line_id}/`, qs.stringify({}))
        .then(() => {
          dispatch(UPDATE_CART_DATA());
        });
    }
    if (!itemData) {
      dispatch(CLEAR_CUSTOMIZATION());
    }
  }, []);

  const divClassNames = useMemo(
    () =>
      [
        'w-full',
        'border',
        'border-solid',
        'border-gray',
        'bg-white',
        'px-4',
        'py-4',
        'text-sm',
        'font-semibold',
        'text-black',
        'md:text-sm',
      ].join(' '),
    []
  );

  if (!showIfNoCustomizationAdded && customization.length === 0) {
    return null;
  }
  return (
    <>
      <DialogBox
        heading={t`Remove Customization`}
        isVisible={isDialogVisible}
        toggleVisible={toggleDialog}
        onAccept={() => clearCustomization()}
      >
        {t`You are about to remove the selected customization from the product.`}
      </DialogBox>
      <div>
        <div className={divClassNames}>
          <div className="mb-4 flex h-6 items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src={UNIID} alt="UNIID" />
              <span>{t`Added print`}</span>
            </div>
            {canBeRemoved && (
              <button
                type="button"
                aria-label={t`Clear Customization`}
                onClick={() => toggleDialog(true)}
                className="m-0 flex items-center border-0 bg-white"
              >
                <RiCloseLine className="h-6 w-6 text-dark-gray" />
              </button>
            )}
          </div>

          {customization &&
            Array.isArray(customization) &&
            customization.length === 0 && (
              <div className="flex items-center justify-between font-normal">
                <span>{t`No customization has been selected.`}</span>
              </div>
            )}
          {customization &&
            Array.isArray(customization) &&
            customization.length >= 1 &&
            customization.map((v) => (
              <div
                className="flex items-center justify-between font-normal"
                key={v.name}
              >
                <span>{v.name}</span>
                <Price
                  unitPrice={v.price}
                  priceSize="sm"
                  priceFontWeight="semibold"
                />
              </div>
            ))}
        </div>
      </div>
    </>
  );
}

CustomizationSummary.propTypes = {
  isRemovable: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  itemData: PropTypes.object,
  productID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showIfNoCustomizationAdded: PropTypes.bool,
};

CustomizationSummary.defaultProps = {
  isRemovable: true,
  itemData: undefined,
  productID: undefined,
  showIfNoCustomizationAdded: true,
};

export default CustomizationSummary;
